import { FC, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApontamentos } from '@/hooks/useApontamentos';
import type { VisaoGeralMes } from '@/types/dashboards';
import { resumoPorMotivo } from '@/utils/equiplexApontamentos';
import { buscaComPeriodo } from '@/utils/filtroPaineis';
import PainelCard from '../PainelCard';
import ApontamentosLista from './ApontamentosLista';

export const ROTA_APONTAMENTOS = '/paineis/equiplex/apontamentos';

const Conteudo: FC<{ mes: VisaoGeralMes; produtos: string }> = ({ mes, produtos }) => {
  const filtro = produtos ? { inicio: mes.start, fim: mes.end, produtos } : { inicio: mes.start, fim: mes.end };
  const { resposta, carregando, erro } = useApontamentos(filtro);
  const lista = useMemo(() => resposta?.apontamentos ?? [], [resposta]);
  const resumo = useMemo(() => resumoPorMotivo(lista), [lista]);

  if (carregando && !resposta) return <p className="text-sm text-content-muted">Carregando os apontamentos…</p>;
  if (erro) return <p className="text-sm text-theme-danger">{erro}</p>;
  if (lista.length === 0) {
    return (
      <p className="text-sm text-content-muted" data-testid="vg-apontamentos-vazio">
        Nenhum apontamento neste mês de produção.
      </p>
    );
  }
  return (
    <>
      <ul className="flex flex-wrap gap-2 text-xs" aria-label="Apontamentos por motivo">
        {resumo.map((r) => (
          <li key={r.motivo} className="rounded-full border border-theme-border px-3 py-1 text-content-secondary">
            {r.rotulo} <strong className="text-content-primary">{r.quantos}</strong>
          </li>
        ))}
      </ul>
      <ApontamentosLista apontamentos={lista} testId="vg-apontamentos-lista" />
    </>
  );
};

/**
 * "Apontamentos da reunião" no pé da Visão geral: o que foi registrado no mês escolhido (e nos
 * produtos do filtro, mais os sem SKU), só leitura. Entra no PDF da reunião junto com o resto.
 */
const ApontamentosDoMes: FC<{ mes: VisaoGeralMes }> = ({ mes }) => {
  const [busca] = useSearchParams();
  const produtos = busca.get('produtos') ?? '';
  return (
    <PainelCard
      testId="vg-apontamentos"
      titulo={`Apontamentos da reunião — ${mes.label}`}
      subtitulo={`${mes.range}. Registrados na plataforma: explicam os desvios, não mudam nenhum número acima.`}
      acoes={
        <Link
          to={`${ROTA_APONTAMENTOS}${buscaComPeriodo(busca.toString(), mes.start, mes.end)}`}
          className="print-hide inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-accent hover:underline"
        >
          <span aria-hidden className="material-symbols-outlined text-[18px] leading-none">edit_note</span>
          Registrar ou editar
        </Link>
      }
    >
      <Conteudo key={`${mes.key}:${mes.start}:${mes.end}:${produtos}`} mes={mes} produtos={produtos} />
    </PainelCard>
  );
};

export default ApontamentosDoMes;
